import { useCallback, useEffect, useState } from 'react'
import {
  Clock,
  MessageSquare,
  RefreshCw,
  Send,
  Smartphone,
  Users,
} from 'lucide-react'
import { useTrail, animated } from '@react-spring/web'

import { Button } from '@/components/common/Button'
import { PageSpinner } from '@/components/common/Spinner'
import { MetricsCard } from '@/components/dashboard/MetricsCard'
import { CampaignSendsChart, VolumeChart, TypesChart } from '@/components/dashboard/VolumeChart'
import AnalyticsService from '@/services/analytics.service'
import type { DashboardMetrics } from '@/types'
import { DASHBOARD_REFRESH_INTERVAL, MESSAGE_PERIOD_OPTIONS } from '@/utils/app-constants'
import { formatDuration } from '@/utils/formatters'

type Period = 'today' | 'yesterday' | 'week' | 'month'

export default function DashboardPage() {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [period, setPeriod] = useState<Period>('today')

  const loadMetrics = useCallback(
    async (silent = false) => {
      if (!silent) setIsLoading(true)
      else setIsRefreshing(true)
      try {
        const data = await AnalyticsService.getDashboardMetrics(period)
        setMetrics(data)
      } finally {
        setIsLoading(false)
        setIsRefreshing(false)
      }
    },
    [period],
  )

  useEffect(() => {
    loadMetrics().catch(() => undefined)
  }, [loadMetrics])

  useEffect(() => {
    const interval = setInterval(() => {
      loadMetrics(true).catch(() => undefined)
    }, DASHBOARD_REFRESH_INTERVAL)
    return () => clearInterval(interval)
  }, [loadMetrics])

  const metricsCards = [
    {
      title: 'Mensagens Hoje',
      value: metrics?.total_messages ?? 0,
      icon: <MessageSquare size={24} />,
      trend: 12,
      color: 'brand' as const,
    },
    {
      title: 'Envios de Campanha',
      value: metrics?.campaign_messages_sent ?? 0,
      icon: <Send size={24} />,
      trend: (metrics?.queued_campaigns ?? 0) + (metrics?.running_campaigns ?? 0),
      trendLabel: 'campanhas ativas',
      color: 'info' as const,
    },
    {
      title: 'Conversas Ativas',
      value: metrics?.total_conversations ?? 0,
      icon: <Users size={24} />,
      trend: 5,
      color: 'success' as const,
    },
    {
      title: 'Taxa de Resposta',
      value:
        metrics?.average_response_time != null
          ? formatDuration(metrics.average_response_time)
          : '--',
      icon: <Clock size={24} />,
      trend: -8,
      trendLabel: 'tempo medio',
      color: 'warning' as const,
    },
    {
      title: 'Instancias Conectadas',
      value: `${metrics?.connected_instances ?? 0}/${metrics?.total_instances ?? 0}`,
      icon: <Smartphone size={24} />,
      trend: 0,
      color: 'info' as const,
    },
  ]

  const trail = useTrail(metricsCards.length, {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: metrics ? 1 : 0, transform: metrics ? 'translateY(0px)' : 'translateY(20px)' },
    config: { tension: 280, friction: 26 },
    delay: 100,
  })

  if (isLoading) {
    return <PageSpinner label="Carregando dashboard..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-[var(--text-muted)]">Visao geral do atendimento e dos disparos.</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex gap-1 p-1 glass rounded-[var(--radius-md)]">
            {MESSAGE_PERIOD_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setPeriod(opt.value as Period)}
                className={`px-3 py-1.5 rounded-[var(--radius-sm)] text-xs font-medium transition-all duration-150 ${
                  period === opt.value
                    ? 'bg-[var(--color-brand)] text-white'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />}
            onClick={() => loadMetrics(true)}
            loading={isRefreshing}
          >
            Atualizar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5 auto-rows-fr items-stretch">
        {trail.map((spring, index) => (
          <animated.div key={metricsCards[index].title} style={spring} className="h-full">
            <MetricsCard
              title={metricsCards[index].title}
              value={metricsCards[index].value}
              icon={metricsCards[index].icon}
              trend={metricsCards[index].trend}
              trendLabel={metricsCards[index].trendLabel}
              color={metricsCards[index].color}
            />
          </animated.div>
        ))}
      </div>

      {((metrics?.running_campaigns ?? 0) > 0 || (metrics?.queued_campaigns ?? 0) > 0) && (
        <div className="glass-card p-4 flex items-center justify-between border-l-4 border-l-[var(--color-brand)]">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[rgba(var(--color-brand-rgb),0.12)] flex items-center justify-center text-[var(--color-brand)]">
              <Send size={16} />
            </span>
            <div>
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                {metrics?.running_campaigns ?? 0} campanha(s) em execucao e {metrics?.queued_campaigns ?? 0} em fila
              </p>
              <p className="text-xs text-[var(--text-muted)]">
                {metrics?.campaign_messages_sent ?? 0} envio(s) de campanha confirmados no periodo
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.location.href = '/monitoring'
            }}
          >
            Ver disparos
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <VolumeChart
          data={metrics?.messages_trend ?? []}
          title="Volume de Mensagens (24h)"
        />
        <CampaignSendsChart
          data={metrics?.campaign_trend ?? []}
          title="Disparos de Campanha"
        />
        <TypesChart
          data={metrics?.messages_by_type ?? {}}
          title="Mensagens por Tipo"
        />
      </div>

      {(metrics?.unread_messages ?? 0) > 0 && (
        <div className="glass-card p-4 flex items-center justify-between border-l-4 border-l-[var(--color-warning)]">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[rgba(245,158,11,0.1)] flex items-center justify-center text-[var(--color-warning)]">
              <MessageSquare size={16} />
            </span>
            <div>
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                {metrics?.unread_messages} mensagens nao lidas
              </p>
              <p className="text-xs text-[var(--text-muted)]">Aguardando atendimento</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              window.location.href = '/chat'
            }}
          >
            Ver mensagens
          </Button>
        </div>
      )}
    </div>
  )
}
